let isRefreshing = false;
let hasShownAlert = false;
let refreshSubscribers = [];
let requestQueue = []; // Queue to store pending requests

function subscribeTokenRefresh(callback) {
    refreshSubscribers.push(callback);
}

function onRefreshed(token) {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
}

import { toast } from "react-toastify";
import { Cookies } from 'react-cookie';

let toastId = null; // Global toast ID
let hasShownTimeout = false; // Prevent multiple toasts

async function fetchWithTimeout(url, options = {}, timeout = 5000, showToast) {
    hasShownTimeout = false; // Reset flag for each request

    const timeoutId = setTimeout(() => {
        if (showToast && !hasShownTimeout) {
            hasShownTimeout = true;

            // Store toast ID
            toastId = toast.info("Your connection is poor, so things are taking longer than usual.", {
                autoClose: 3000,
                position: "top-center",
                hideProgressBar: false,
                closeOnClick: true,
                draggable: true,
                pauseOnHover: false,
                pauseOnFocusLoss: false,
            });

            toastId = null;
            hasShownTimeout = false;
        }
    }, timeout);

    try {
        const response = await fetch(url, options);
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

async function processQueue(token) {
    while (requestQueue.length > 0) {
        const { url, options, resolve, reject, showToast } = requestQueue.shift();
        try {
            const newOptions = {
                ...options,
                headers: {
                    ...options.headers,
                    'Authorization': `Bearer ${token}`,
                },
            };
            const response = await fetchWithTimeout(url, newOptions, 5000, showToast);
            resolve(response);
        } catch (error) {
            reject(error);
        }
    }
}

export async function fetchWithRefresh(url, options = {}, logout, updateTokens, showToast) {
    return new Promise((resolve, reject) => {
        (async () => {
            try {
                const response = await fetchWithTimeout(url, options, 5000, showToast);

                if (response.status !== 401) {
                    resolve(response);
                    return;
                }

                // If 401, queue the request
                requestQueue.push({ url, options, resolve, reject, showToast });

                if (isRefreshing) {
                    return;
                }

                isRefreshing = true;

                try {
                    const cookies = new Cookies();
                    const authToken = localStorage.getItem('auth_session');
                    const refreshToken = cookies.get('refresh_token');

                    if (!refreshToken) {
                            if (logout) logout();
                            if (!hasShownAlert) {
                                alert('Session expired. Please log in again.');
                                hasShownAlert = true;
                            }
                            throw new Error('Session expired. Please log in again.');
                    }

                    const refreshResponse = await fetchWithTimeout(
                        import.meta.env.VITE_API_URL + 'Users/RefreshToken',
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${authToken}`,
                            },
                            body: JSON.stringify({ refreshToken }),
                        },
                        5000,
                        showToast
                    );

                    if (!refreshResponse.ok) {
                        if (logout) logout();
                        if (!hasShownAlert) {
                            toast.error('Session expired. Please log in again.', {
                                autoClose: 3000,
                                position: "top-center",
                            });
                            hasShownAlert = true;
                        }
                        throw new Error('Session expired. Please log in again.');
                    }

                    const { token, refreshToken: newRefreshToken } = await refreshResponse.json();

                    if (updateTokens) await updateTokens(token, newRefreshToken);

                    onRefreshed(token);
                    await processQueue(token);
                } catch (error) {
                    if (!hasShownAlert) {
                        toast.error('We encountered an issue while refreshing your session. Please try again.', {
                            autoClose: 3000,
                            position: "top-center",
                        });
                        hasShownAlert = true;
                    }
                    requestQueue.forEach(({ reject }) => reject(error));
                    requestQueue = [];
                } finally {
                    isRefreshing = false;
                }
            } catch (error) {
                reject(error);
            }
        })();
    });
}

export default fetchWithRefresh;
